import { useState, useEffect } from 'react';
import { isInstalledPWA, markPWAInstalledIfStandalone } from '@/utils/pwa';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(() => isInstalledPWA());
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const checkInstalled = () => {
      setIsInstalled(isInstalledPWA());
      markPWAInstalledIfStandalone();
    };

    const markInstalled = () => {
      setIsInstalled(true);
      try { window.localStorage.setItem('lupuslogix_pwa_installed', 'true'); } catch { /* ignore */ }
    };

    checkInstalled();

    const mediaQueries = [
      window.matchMedia('(display-mode: standalone)'),
      window.matchMedia('(display-mode: fullscreen)'),
      window.matchMedia('(display-mode: minimal-ui)'),
    ];
    mediaQueries.forEach((mq) => mq.addEventListener?.('change', checkInstalled));

    const raf = window.requestAnimationFrame(checkInstalled);
    const shortTimer = window.setTimeout(checkInstalled, 250);
    const longTimer = window.setTimeout(checkInstalled, 1000);

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    setIsIOS(iOS);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // `beforeinstallprompt` only fires when the browser considers the site
      // installable AND not currently installed. If we see it, any stale
      // "installed" flag from a prior session (or an uninstall) is wrong —
      // clear it so the Getting Started checklist reflects reality.
      if (!isInstalledPWA()) {
        try {
          window.localStorage.removeItem('lupuslogix_pwa_installed');
          window.localStorage.removeItem('lupuslogix_post_install_shown');
        } catch { /* ignore */ }
        setIsInstalled(false);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkInstalled();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', markInstalled);
    window.addEventListener('focus', checkInstalled);
    window.addEventListener('pageshow', checkInstalled);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', markInstalled);
      window.removeEventListener('focus', checkInstalled);
      window.removeEventListener('pageshow', checkInstalled);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      mediaQueries.forEach((mq) => mq.removeEventListener?.('change', checkInstalled));
      window.cancelAnimationFrame(raf);
      window.clearTimeout(shortTimer);
      window.clearTimeout(longTimer);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) {
      return false;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      // Do NOT flip isInstalled here. Chrome/Edge fires `userChoice` the
      // moment the user taps "Install" in the mini-infobar, well before the
      // OS finishes installing and adding the icon. The `appinstalled` event
      // (wired in the effect above) and the standalone display-mode check
      // are the only reliable signals — wait for them so the "You're all set"
      // card only shows after the install truly completes.
      setDeferredPrompt(null);
      return true;
    }

    return false;
  };

  return {
    deferredPrompt,
    isInstalled,
    isIOS,
    promptInstall,
    canInstall: !isInstalled && (!!deferredPrompt || isIOS)
  };
};
